import { useIntersection } from '@mantine/hooks';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useInfiniteQuery } from '@tanstack/react-query';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import { CoreFieldEnumOpt } from 'providers/contexts/extensions/DynamicFieldData';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useDataProvider } from 'react-admin';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { DenumFieldDropdown } from './DenumFieldDropdown';
import { FieldOptions, TDynamicFieldOnChange } from '../DynamicFormField';
import { handleToggle } from '../utility/functions';
import { useDynamicFieldContext } from '../DynamicFieldContext';
import { DomainField } from './DomainField';

type TFetchDenumOptionsParams = {
  pageParam: string | number;
};

export type DenumFieldProps = {
  value: string | string[];
  onChange: TDynamicFieldOnChange;
  onValidationChange?: (fieldName: string, isValid: boolean) => void;
  disabled?: boolean;
  fieldOptions?: FieldOptions;
  fieldSx?: SxProps<Theme>;
};

export const DenumField = memo(
  ({
    value,
    onChange,
    onValidationChange,
    disabled,
    fieldOptions,
    fieldSx,
  }: DenumFieldProps) => {
    const {
      selectedValues,
      updateSelectedValues,
      fieldConfig,
      searchText,
      cleanDbSearchText,
      dynamicOptions,
      fetchedOptions,
      updateDynamicOptions,
      updateSearchText,
      updateFetchedOptions,
    } = useDynamicFieldContext();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const isMultiSelect = Boolean(fieldConfig?.config?.multiple);

    // CMSADMIN-2045: Domain/hostname field special ux case
    // Alternate denum display options
    const isDomainView = fieldOptions?.denumView === 'domain';
    const isDropdownView = fieldOptions?.denumView === 'dropdown';

    // Intersection observer for infinite scroll opts
    const { ref: ixRef, entry } = useIntersection({ threshold: 0.5 });

    // Sync value when props change
    useEffect(() => {
      if (isMultiSelect) {
        const arrayValue = Array.isArray(value) ? value : value ? [value] : [];
        updateSelectedValues(arrayValue);
      } else {
        // Handle single select.
        const stringValue = Array.isArray(value)
          ? value[0] || ''
          : (value as string) || '';
        updateSelectedValues(stringValue);
      }
    }, [value, isMultiSelect, updateSelectedValues]);

    // Infinite query to fetch dynamic options
    const {
      data: denumData,
      fetchNextPage: fetchNextDenumPage,
      isSuccess: isDenumSuccess,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
    } = useInfiniteQuery({
      queryKey: [
        'getDynamicEnumerationData',
        cleanDbSearchText,
        fieldConfig?.config?.resolver,
        fieldConfig?.config?.app,
        fieldConfig?.config?.params,
      ],
      queryFn: async ({ pageParam }: TFetchDenumOptionsParams) => {
        const params = {
          ...fieldConfig?.config?.params,
          ...(cleanDbSearchText ? { query: cleanDbSearchText } : {}),
        };
        // Fetch dynamic enumeration options from API
        const response = await dataProvider.getDynamicEnumerationData({
          app: fieldConfig?.config?.app,
          resolver: fieldConfig?.config?.resolver,
          limit: 99,
          page: pageParam,
          params: params,
        });
        // Return data
        const respItems = response?.data || {};
        return respItems;
      },
      initialPageParam: '0',
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.next;
      },
      getPreviousPageParam: (lastPage, allPages) => {
        return lastPage?.prev;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    // note: bug in api sometimes nulls for total...
    // can also go null in between typeaheads
    const apiTotal = useMemo(() => {
      return denumData?.pages?.[0]?.total ?? null;
    }, [denumData]);

    // Flatten all inf query pages to options array
    const flatData = useMemo<CoreFieldEnumOpt[]>(() => {
      if (!denumData) {
        return [];
      }
      const flatOpts = denumData.pages.flatMap((page) => {
        const items = page?.items || [];
        return items;
      });
      return flatOpts;
    }, [denumData]);

    /**
     * Update dynamic options states when data
     * is fetched and the flat data is computed.
     *
     * Prevents dropdown going blank while
     * inflight fetch of a typeahead query.
     */
    useEffect(() => {
      if (isDenumSuccess && flatData) {
        updateFetchedOptions(flatData);
        updateDynamicOptions(flatData);
      }
    }, [isDenumSuccess, flatData, updateFetchedOptions, updateDynamicOptions]);

    // Handle select / deselect options
    const onToggle = useCallback(
      (optionValue: string) => {
        if (isMultiSelect) {
          const newSelectedValues = handleToggle(
            selectedValues as string[],
            optionValue,
            false,
          );
          updateSelectedValues(newSelectedValues);
          onChange(fieldConfig.name, newSelectedValues, { options: dynamicOptions });
        } else {
          updateSelectedValues(optionValue);
          onChange(fieldConfig.name, optionValue, { options: dynamicOptions });
        }
      },
      [
        dynamicOptions,
        fieldConfig.name,
        isMultiSelect,
        onChange,
        selectedValues,
        updateSelectedValues,
      ],
    );

    // CMSADMIN-2045: Render DomainField for domain/hostname fields.
    if (isDomainView) {
      return (
        <DomainField
          fieldSx={fieldSx}
          value={value}
          onChange={onChange}
          onValidationChange={onValidationChange}
        />
      );
    }

    // Alternative denum display mode - contained dropdown
    // this can be used in space limited contexts like modals
    if (isDropdownView) {
      return (
        <DenumFieldDropdown
          fieldConfig={fieldConfig}
          selectedValues={selectedValues}
          dynamicOptions={dynamicOptions}
          fetchedOptions={fetchedOptions}
          searchText={searchText}
          updateSearchText={updateSearchText}
          disabled={disabled}
          fieldSx={fieldSx}
          onToggle={onToggle}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextDenumPage}
          isFetching={isFetching}
          apiTotal={apiTotal}
        />
      );
    }

    // Standard denum display - checkboxes for multi, radios for single
    // Note: chips is in DynamicFormField: <SelectedItemsAccordion>
    return (
      <FormControl
        fullWidth
        variant="outlined"
        data-testid={`denum-field--${fieldConfig.name}`}
        sx={fieldSx}
      >
        {dynamicOptions.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 2, textAlign: 'center' }}
          >
            {isFetching ? <LinearProgress /> : `No ${fieldConfig.label} found`}
          </Typography>
        ) : isMultiSelect ? (
          // Multi select
          <Box data-testid="denum-multi-select-container">
            <Stack spacing={0}>
              {dynamicOptions.map((option) => (
                <FormControlLabel
                  key={option?.value}
                  label={option?.label}
                  control={
                    <Checkbox
                      checked={(selectedValues as string[]).includes(option?.value)}
                      onChange={() => onToggle(option?.value)}
                      size="small"
                    />
                  }
                />
              ))}
            </Stack>
            <IntersectionLoader
              hasNextPage={hasNextPage}
              isFetching={isFetchingNextPage}
              fetchNextPage={fetchNextDenumPage}
              hideOnPagesEnd={true}
              ixRef={ixRef}
              entry={entry}
              compact={true}
            />
          </Box>
        ) : (
          // Single select
          <Box data-testid="denum-single-select-container">
            <RadioGroup
              value={selectedValues as string}
              onChange={(e) => onToggle(e.target.value)}
            >
              {dynamicOptions.map((option) => (
                <FormControlLabel
                  key={option?.value}
                  value={option?.value}
                  control={<Radio size="small" />}
                  label={option?.label}
                />
              ))}
            </RadioGroup>
            <IntersectionLoader
              hasNextPage={hasNextPage}
              isFetching={isFetchingNextPage}
              fetchNextPage={fetchNextDenumPage}
              hideOnPagesEnd={true}
              ixRef={ixRef}
              entry={entry}
              compact={true}
            />
          </Box>
        )}
      </FormControl>
    );
  },
);
DenumField.displayName = 'DenumField';
